import { FC } from "react";
import { ICancellationStats } from "../../../models/statistcs/respons";
import OrderStatusStatsCard from "../order-status-stats-card";

interface IProps {
  stats?: ICancellationStats;
  onChange: (stats: ICancellationStats) => void;
  loadStats?: (query: { startDate?: string; endDate?: string }) => Promise<{
    data: ICancellationStats;
  }>;
  titleKey: string;
  cancelledField: "customerCancelledCount" | "administrativeCancelledCount";
  cancelledLabelKey: string;
  averageLabelKey?: string;
  showCauses?: boolean;
  hideDateControls?: boolean;
}

const groupColumns = [
  { labelKey: "orders", field: "orderCount", width: 110 },
  {
    labelKey: "average",
    field: "averageCancellationMinutes",
    format: "minutes" as const,
    width: 140,
  },
  {
    labelKey: "median",
    field: "medianCancellationMinutes",
    format: "minutes" as const,
    width: 140,
  },
  {
    labelKey: "p90",
    field: "p90CancellationMinutes",
    format: "minutes" as const,
    width: 140,
  },
  {
    labelKey: "product_change_rate",
    field: "productChangeOrderRate",
    format: "percent" as const,
    width: 160,
  },
  {
    labelKey: "unavailable_product_order_rate",
    field: "unavailableProductOrderRate",
    format: "percent" as const,
    width: 190,
  },
  { labelKey: "price_change_orders", field: "priceChangeOrderCount", width: 160 },
  { labelKey: "missing_reason", field: "missingReasonCount", width: 150 },
  {
    labelKey: "repeated_cancellations",
    field: "repeatedCancellationCount",
    width: 180,
  },
];

const CancellationStatsCard: FC<IProps> = ({
  stats,
  onChange,
  loadStats,
  titleKey,
  cancelledField,
  cancelledLabelKey,
  averageLabelKey = "average_cancellation_time",
  showCauses,
  hideDateControls,
}) => (
  <OrderStatusStatsCard
    stats={stats}
    onChange={onChange}
    loadStats={loadStats}
    hideDateControls={hideDateControls}
    titleKey={titleKey}
    averageLabelKey={averageLabelKey}
    averageField="averageCancellationMinutes"
    includedField={cancelledField}
    includedLabelKey={cancelledLabelKey}
    excludedField="excludedOrderCount"
    excludedTooltipKey="excluded_cancellation_orders_tooltip"
    metrics={[
      { labelKey: "period_orders", field: "periodOrderCount" },
      {
        labelKey: "candidate_cancellations",
        field: "candidateCancellationCount",
      },
      {
        labelKey: "cancellation_rate",
        field: "cancellationRate",
        format: "percent",
      },
      {
        labelKey: "data_quality_rate",
        field: "cancellationDataQualityRate",
        format: "percent",
      },
      { labelKey: "median", field: "medianCancellationMinutes", format: "minutes" },
      { labelKey: "p90", field: "p90CancellationMinutes", format: "minutes" },
      {
        labelKey: "product_change_rate",
        field: "productChangeOrderRate",
        format: "percent",
      },
      {
        labelKey: "unavailable_product_order_rate",
        field: "unavailableProductOrderRate",
        format: "percent",
      },
      {
        labelKey: "price_change_rate",
        field: "priceChangeOrderRate",
        format: "percent",
      },
      { labelKey: "missing_reason", field: "missingReasonCount" },
      {
        labelKey: "repeated_cancellations",
        field: "repeatedCancellationCount",
      },
    ]}
    tables={[
      {
        key: "areas",
        labelKey: "areas",
        rows: (value) => [
          ...(value.areas || []),
          ...(value.outsideAreas ? [value.outsideAreas] : []),
        ],
        rowKey: (row, index) => `area-${row.areaId ?? index}`,
        columns: [{ labelKey: "area", field: "areaName", width: 220 }, ...groupColumns],
      },
      {
        key: "reasonTypes",
        labelKey: "cancellation_reasons",
        rows: (value) => value.reasonTypes || [],
        rowKey: (row, index) => `reason-${row.reasonCancellationType ?? index}`,
        columns: [
          {
            labelKey: "reason",
            field: "reasonCancellationTypeLabel",
            width: 260,
          },
          ...groupColumns,
        ],
      },
      ...(showCauses
        ? [
            {
              key: "causes",
              labelKey: "inferred_causes",
              rows: (value: ICancellationStats) => value.causes || [],
              rowKey: (row: Record<string, any>, index: number) =>
                `cause-${row.inferredCause ?? index}`,
              columns: [
                {
                  labelKey: "cause",
                  field: "inferredCauseLabel",
                  width: 260,
                },
                { labelKey: "evidence", field: "evidenceLabel", width: 260 },
                ...groupColumns,
              ],
            },
          ]
        : []),
      {
        key: "previousStatuses",
        labelKey: "previous_statuses",
        rows: (value) => value.previousStatuses || [],
        rowKey: (row, index) => `status-${row.previousStatus ?? index}`,
        columns: [
          {
            labelKey: "previous_status",
            field: "previousStatusLabel",
            width: 240,
          },
          ...groupColumns,
        ],
      },
    ]}
  />
);

export default CancellationStatsCard;
